import React, { useState, memo } from 'react';
import moment from 'moment-timezone';
import { type Position, type ProtectedUser, type Suspect } from '../api/types.js';
import { useSettings } from '../SettingsProvider.js';
import { useTranslation } from '../i18n/useTranslation.js';
import { getFlagEmoji } from '../utils/Helpers.js';
import { CountryNames, ISO3to2Mapping } from '../utils/Countries.js';

interface SuspectProfileProps {
  s: Suspect;
  pos: Position;
  selected: boolean;
  onClick: () => void;
  onFlyTo: () => void;
}

const SuspectRow = memo(function SuspectRow({ s, pos, selected, onClick, onFlyTo }: SuspectProfileProps) {
  const { settings } = useSettings();
  const { t } = useTranslation();

  const score = pos?.score ?? s.baseScore;
  const status = pos?.status;
  const statusLabel = {
    breach: t('watchlist.statusBreach'),
    inzone: t('watchlist.statusInzone'),
    approach: t('watchlist.statusApproach'),
    active: t('watchlist.statusActive'),
    watch: t('watchlist.statusWatch'),
    flagged: t('watchlist.statusFlagged'),
  };
  const statusClass = {
    breach: 'r blink',
    inzone: 'r',
    approach: 'o',
    active: 'r',
    watch: 'o',
    flagged: 'b',
  };

  // Get key apps to display (max 2, mark hostile ones)
  const keyApps = (s.apps || []).slice(0, 2).map((app) => ({
    name: app.name || app.bundle,
    hot: (s.hostileApps || []).includes(app),
  }));

  return (
    <tr className={selected ? 'sel' : ''} onClick={onClick}>
      <td>
        <span className={`tid ${s.level}`}>{s.id}</span>
      </td>
      {/* TODO: Return once we have it */}
      <td>
        <div className="score-cell">
          <div className="sb2">
            <div className={`sf2 ${s.level}`} style={{ width: `${score}%` }} />
          </div>
          <span className="sn">{score}</span>
        </div>
      </td>
      <td className="t2">{s.profiles?.join(', ')}</td>
      <td>
        <span className={`ats ${s.hostileCountries?.includes(s.countries[0]) ? 'hot' : ''}`}>{s.origin}</span>
      </td>
      <td className={`mono ${s.hostileLang ? 'text-hot' : ''}`}>{s.lang || 'EN'}</td>
      <td>
        {keyApps.map((app, i) => (
          <span key={i} className={`ats ${app.hot ? 'hot' : ''}`}>
            {app.hot && '⚠ '}
            {app.name}
          </span>
        ))}
      </td>
      <td className="mono t3">
        {s.lat.toFixed(4)}, {s.lng.toFixed(4)}
      </td>
      <td>
        <span className={`spill ${statusClass[status]}`}>{statusLabel[status]}</span>
      </td>
      <td className="mono last-seen">
        {moment
          .tz(s.lastSeen, settings?.general.timezone ?? 'UTC')
          .format(`${settings?.general.dateFormat ?? 'YYYY-MM-DD'} HH:mm:ss`)}
      </td>
      <td className="actions">
        <button
          className="act-btn"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          👤
        </button>
        <button
          className="act-btn"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          ⏱
        </button>
        <button
          className="act-btn"
          onClick={(e) => {
            e.stopPropagation();
            onFlyTo();
          }}
        >
          🗺
        </button>
      </td>
    </tr>
  );
});

const ProtectedUserRow = memo(function ProtectedUserRow({ u, onFlyTo }: { u: ProtectedUser; onFlyTo: () => void }) {
  const { settings } = useSettings();
  const countryDisplay = u.cc ? `${CountryNames[ISO3to2Mapping[u.cc.toUpperCase()]]} ${getFlagEmoji(u.cc)}` : '—';
  return (
    <tr>
      <td className="mono t2">{u.userId || u.objectId}</td>
      <td className="mono t3">
        {u.lat.toFixed(4)}, {u.lng.toFixed(4)}
      </td>
      <td>{countryDisplay}</td>
      <td className="mono t3" style={{ fontSize: 10 }}>
        {u.bundle || '—'}
      </td>
      <td className="mono last-seen">
        {moment
          .tz(u.ts, settings?.general.timezone ?? 'UTC')
          .format(`${settings?.general.dateFormat ?? 'YYYY-MM-DD'} HH:mm:ss`)}
      </td>
      <td className="actions">
        <button className="act-btn" onClick={onFlyTo}>
          🗺
        </button>
      </td>
    </tr>
  );
});

interface WatchlistPanelProps {
  suspectsArray: Array<Suspect>;
  positions: Record<string, Position>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onFlyTo: (loc: any) => void;
  protectedUsers: Array<ProtectedUser>;
}

function WatchlistPanel({
  suspectsArray,
  positions,
  selectedId,
  onSelect,
  onFlyTo,
  protectedUsers,
}: WatchlistPanelProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('watchlist');
  const [filters, setFilters] = useState({ high: false, origin: false, apps: false, recent: false, text: '' });

  const sortedSuspects = suspectsArray.sort(
    (a, b) => (positions[b.id]?.score ?? b.baseScore) - (positions[a.id]?.score ?? a.baseScore)
  );

  const filteredSuspects = sortedSuspects.filter((s) => {
    if (
      filters.text &&
      !new RegExp(filters.text, 'gi').test(s.id) &&
      !s.lat.toString().startsWith(filters.text) &&
      !s.lng.toString().startsWith(filters.text)
    ) {
      return false;
    }
    if (filters.high && s.level !== 'h') return false;
    if (filters.origin && !s.hostileCountries?.length) return false;
    if (filters.apps && !s.hostileApps?.length) return false;
    if (filters.recent && Date.now() - s.lastSeen > 72 * 60 * 60 * 1000) return false;
    return true;
  });

  const filteredProtectedUsers = protectedUsers.filter((s) => {
    if (
      filters.text &&
      !new RegExp(filters.text, 'gi').test(s.userId) &&
      !new RegExp(filters.text, 'gi').test(s.bundle) &&
      !s.lat.toString().startsWith(filters.text) &&
      !s.lng.toString().startsWith(filters.text)
    ) {
      return false;
    }
    if (filters.recent && Date.now() - s.ts > 72 * 60 * 60 * 1000) return false;
    return true;
  });

  const toggleFilter = (key: string) => setFilters((f) => ({ ...f, [key]: !f[key] }));
  const updateTextFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters((f) => ({ ...f, text: value }));
  };

  return (
    <div className="bp">
      <div className="btb">
        <div className="bts">
          {[
            { id: 'watchlist', label: t('watchlist.tabWatchlist', { count: suspectsArray.length }) },
            { id: 'protectedUsers', label: t('watchlist.tabProtectedUsers', { count: protectedUsers.length }) },
            { id: 'groups', label: t('watchlist.tabGroups') },
            { id: 'timelapse', label: t('watchlist.tabTimelapse') },
            { id: 'geo', label: t('watchlist.tabGeo') },
          ].map((tab) => (
            <button
              key={tab.id}
              className={`btab ${activeTab === tab.id ? 'on' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab === 'watchlist' && (
          <>
            <div className={`fb ${filters.high ? 'on' : ''}`} onClick={() => toggleFilter('high')}>
              {t('watchlist.filterHigh')}
            </div>
            <div className={`fb ${filters.origin ? 'on' : ''}`} onClick={() => toggleFilter('origin')}>
              {t('watchlist.filterOrigin')}
            </div>
            <div className={`fb ${filters.apps ? 'on' : ''}`} onClick={() => toggleFilter('apps')}>
              {t('watchlist.filterApps')}
            </div>
          </>
        )}
        <div className={`fb ${filters.recent ? 'on' : ''}`} onClick={() => toggleFilter('recent')}>
          {t('watchlist.filterRecent')}
        </div>
        <div className="sbox">
          <span style={{ fontSize: 12, color: 'var(--t3)' }}>🔍</span>
          <input type="text" placeholder={t('watchlist.searchPlaceholder')} onChange={updateTextFilter} />
        </div>
      </div>

      {activeTab === 'watchlist' && (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>{t('watchlist.colId')}</th>
                {/* TODO: Return once we have it */}
                <th>{t('watchlist.colScore')}</th>
                <th>{t('watchlist.colProfile')}</th>
                <th>{t('watchlist.colOrigin')}</th>
                <th>{t('watchlist.colDeviceLang')}</th>
                <th>{t('watchlist.colKeyApps')}</th>
                <th>{t('watchlist.colLastLocation')}</th>
                <th>{t('watchlist.colStatus')}</th>
                <th>{t('watchlist.colLastSeen')}</th>
                <th>{t('watchlist.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuspects.map((s) => (
                <SuspectRow
                  key={s.id}
                  s={s}
                  pos={positions[s.id]}
                  selected={selectedId === s.id}
                  onClick={() => onSelect(s.id)}
                  onFlyTo={() => onFlyTo({ lat: positions[s.id]?.lat, lng: positions[s.id]?.lng, zoom: 19 })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'protectedUsers' && (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>{t('watchlist.colUserId')}</th>
                <th>{t('watchlist.colLastLocation')}</th>
                <th>{t('watchlist.colCountry')}</th>
                <th>{t('watchlist.colBundle')}</th>
                <th>{t('watchlist.colLastSeen')}</th>
                <th>{t('watchlist.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredProtectedUsers.map((u) => (
                <ProtectedUserRow
                  key={u.objectId}
                  u={u}
                  onFlyTo={() => onFlyTo({ lat: u.lat, lng: u.lng, zoom: 17 })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab !== 'watchlist' && activeTab !== 'protectedUsers' && (
        <div className="tw placeholder">
          <div className="placeholder-text">
            {activeTab === 'groups' && t('watchlist.placeholderGroups')}
            {activeTab === 'timelapse' && t('watchlist.placeholderTimelapse')}
            {activeTab === 'geo' && t('watchlist.placeholderGeo')}
          </div>
        </div>
      )}
    </div>
  );
}

export default WatchlistPanel;
